import * as L from 'leaflet';
import 'leaflet.markercluster';

import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';

import { Router } from '@angular/router';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';

import {
  AssetsService,
  I18nService,
  Location,
  MarkerMode,
  SensorStatus,
  UiService,
  swipe,
  GLOBAL_CONSTANT,
  ZOOM_BUILDING_THRESHOLD,
  ZOOM_CIRCLES_THRESHOLD,
  ZOOM_PLANT_THRESHOLD,
  ZOOM_MAX_THRESHOLD,
  App_Routes,
  logError,
} from '@lp-ui-lib';

import { SpinnerComponent } from '@lp-ui-lib';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [NzCollapseModule, NzIconModule, SpinnerComponent],
  templateUrl: './map.html',
  styleUrls: ['./map.scss'],
})
export class MapComponent implements AfterViewInit, OnDestroy {
  readonly plants = input<Location[]>([]);
  readonly loading = input<boolean>(false);
  readonly satelliteView = input<boolean>(false);
  readonly infoOverlay = input<boolean>(false);

  private router = inject(Router);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  private assetsService = inject(AssetsService);

  GLOBAL_CONSTANT = GLOBAL_CONSTANT;

  // Outputs
  markerClick = output<Location>();

  // Reactive state
  readonly zoomLevel = signal(this.GLOBAL_CONSTANT.ZERO);
  readonly markerMode = signal<MarkerMode>(MarkerMode.dots);
  readonly isInit = signal(false);
  readonly sortedPlantList = signal<Location[]>([]);
  readonly isPanelOpen = signal(false);
  readonly isnzActive = signal(false);
  readonly autoFit = signal(true);

  private readonly LANDING_ZOOM = 6;
  private readonly LANDING_CENTER: L.LatLngExpression = [51.175545, 10.2180081]; // keep your center
  readonly landingViewDone = signal(false);

  Map: L.Map | null = null;
  tileLayer: L.TileLayer | null = null;
  markers: any = null;
  private resizeObserver?: ResizeObserver;
  private mapContainerEl?: HTMLElement;

  readonly computedMarkerMode = computed(() => {
    const zoom = this.zoomLevel();
    if (zoom < ZOOM_CIRCLES_THRESHOLD) return MarkerMode.dots;
    if (zoom < ZOOM_PLANT_THRESHOLD) return MarkerMode.circles;
    if (zoom < ZOOM_BUILDING_THRESHOLD) return MarkerMode.plants;
    return MarkerMode.buildings;
  });

  readonly isMobile = computed(() => this.ui.IsMobile());
  readonly isTablet = computed(() => this.ui.IsTablet());
  readonly isDesktop = computed(() => this.ui.IsDesktop());
  markerLatLngs: any;

  ngAfterViewInit() {
    this.initMap();
    this.refreshMapSize(200);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    if (this.Map) {
      this.Map.off();
      this.Map.remove();
      this.Map = null;
    }
  }

  constructor() {
    effect(() => {
      const p = this.plants();
      this.loadPanelList(p);
    });
    // effect(() => {
    //   this.markerMode();
    //   if (!this.isInit() || !this.Map) return;
    //   this.addMarkers();
    // });
    effect(() => {
      const plants = this.plants();
      this.loadPanelList(plants);
      if (!this.isInit() || !this.Map) return;
      this.addMarkers();
      // Only auto-fit if you explicitly re-enable it later (e.g., a "fit all" button)
      if (this.autoFit()) {
        queueMicrotask(() => this.fitBounds());
      }
    });

    effect(() => {
      if (!this.isInit() || !this.Map) return;

      this.assetsService.getMapBoxKeys().then((keys) => {
        this.addTiles(keys);
      });
    });
    effect(() => {
      const useSatellite = this.satelliteView();
      if (!this.isInit() || !this.Map) return;
      this.assetsService
        .getMapBoxKeys()
        .then((keys) => {
          if (!this.Map) return;
          this.addTiles(keys, useSatellite);
        })
        .catch((error) => {
          logError(error);
        });
    });
  }

  showArrow(event: boolean) {
    this.isnzActive.set(event);
  }

  onSwipe(e: TouchEvent, when: 'start' | 'end'): void {
    swipe(e, when);
    this.isPanelOpen.set(!this.isPanelOpen());
  }

  navigate(plant: Location) {
    this.router.navigate([App_Routes.PLANT, plant.objectID]);
  }

  private loadPanelList(plants: Location[]) {
    const sortInactive: Location[] = [];
    const sortCritical: Location[] = [];
    const sortWarning: Location[] = [];
    const sortWorking: Location[] = [];

    plants.forEach((plant: any) => {
      if (plant?.numSensors > 0) {
        if (plant.InActive?.length > 0) {
          plant.icon = SensorStatus.INACTIVE;
          sortInactive.push(plant);
        } else if (plant.Critical?.length > 0) {
          plant.icon = SensorStatus.CRITICAL;
          sortCritical.push(plant);
        } else if (plant.Warning?.length > 0) {
          plant.icon = SensorStatus.WARNING;
          sortWarning.push(plant);
        } else if (plant.Working?.length > 0) {
          plant.icon = SensorStatus.WORKING;
          sortWorking.push(plant);
        }
      }
    });

    const byName = (a: any, b: any) => a.objectName.localeCompare(b.objectName);

    this.sortedPlantList.set([
      ...sortInactive.sort(byName),
      ...sortCritical.sort(byName),
      ...sortWarning.sort(byName),
      ...sortWorking.sort(byName),
    ]);
  }

  private initMap() {
    const el = document.getElementById('Map');
    if (!el) return;

    if (this.Map) {
      this.Map.off();
      this.Map.remove();
      this.Map = null;
    }

    this.Map = L.map('Map', {
      zoomControl: false,
      boxZoom: true,
      zoomAnimation: true,
    });
    this.mapContainerEl = document.getElementById('Map') as HTMLElement;

    // ResizeObserver: whenever container size changes, refresh Leaflet
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      // if size is changing due to CSS transitions, this will fire multiple times — OK
      this.refreshMapSize();
    });
    if (this.mapContainerEl) this.resizeObserver.observe(this.mapContainerEl);

    // When tab becomes visible again / navigating back with bfcache,
    // call invalidateSize so the map fills the container.
    window.addEventListener('pageshow', () => this.refreshMapSize(50));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshMapSize(50);
    });

    // Disable auto-fit after any user interaction
    this.Map.on('zoomstart dragstart mousedown touchstart', () => {
      this.autoFit.set(false);
    });
    this.Map.on('zoomend', () => {
      if (!this.Map) return;

      const zoom = this.Map.getZoom();

      const newMode =
        zoom < ZOOM_CIRCLES_THRESHOLD
          ? MarkerMode.dots
          : zoom < ZOOM_PLANT_THRESHOLD
            ? MarkerMode.circles
            : zoom < ZOOM_BUILDING_THRESHOLD
              ? MarkerMode.plants
              : MarkerMode.buildings;

      if (newMode !== this.markerMode()) {
        this.markerMode.set(newMode);
        this.addMarkers();
      }

      this.zoomLevel.set(zoom);
    });

    this.assetsService.getMapBoxKeys().then((keys) => {
      this.tileLayer = null;
      this.addTiles(keys);

      setTimeout(() => {
        this.Map?.invalidateSize();
        this.Map?.setView(this.LANDING_CENTER, this.LANDING_ZOOM);
        this.zoomLevel.set(this.LANDING_ZOOM);
        this.landingViewDone.set(true);
        // Start in "no auto-fit" mode until user interacts:
        this.autoFit.set(false);
        this.isInit.set(true);
        this.addMarkers();
        this.tileLayer?.once('load', () => {
          this.Map?.invalidateSize({ pan: false });
        });
      }, 0);
    });
  }

  private addTiles(mapboxKeys: any, useSatellite = this.satelliteView()) {
    if (!this.Map) return;

    // remove old tiles
    if (this.tileLayer) {
      try {
        this.Map.removeLayer(this.tileLayer);
      } catch (e) {
        logError(e);
      }
      this.tileLayer = null;
    }

    // Your backend already provides a full tile template URL
    // Example: .../tiles/512/{z}/{x}/{y}@2x?access_token=pk...
    let template = useSatellite ? mapboxKeys.satelliteView : mapboxKeys.mapView;

    if (!template) {
      console.error('[Map] Missing mapView/satelliteView in mapboxKeys', mapboxKeys);
      return;
    }

    // Important: if backend or previous code encoded the braces, decode them
    // Leaflet needs literal {z}/{x}/{y} placeholders to substitute tile coords. [1](https://stackoverflow.com/questions/13638969/using-custom-map-image-tiles-in-leafletjs)[2](https://gis.stackexchange.com/questions/347646/specify-an-alternative-url-for-a-tilelayer-to-use-in-leaflet)
    template = decodeURIComponent(String(template));

    // If template does NOT already contain access_token, add it once
    // (your screenshot shows it already exists, so this won't run)
    if (!template.includes('access_token=')) {
      const token = mapboxKeys.geoCodingToken ?? mapboxKeys.accessToken ?? mapboxKeys.token;
      if (!token) {
        console.error('[Map] Missing token AND template has no access_token', mapboxKeys);
        return;
      }
      template += (template.includes('?') ? '&' : '?') + `access_token=${token}`;
    }

    this.tileLayer = L.tileLayer(template, {
      maxZoom: 18,
      tileSize: 512,
      zoomOffset: -1,
      minZoom: 1,
      noWrap: true,
      bounds: [
        [-90, -180],
        [90, 180],
      ],
      opacity: 1,
    });

    this.tileLayer.addTo(this.Map);
  }
  private fitBounds(global = false, checkForMarkerModeChange = true): void {
    if (!this.markers || !this.Map) return;

    const layers = this.markers.getLayers?.() ?? [];
    if (!layers.length) return;

    const bounds = L.latLngBounds(layers.map((item: any) => item._latlng));
    if (!bounds.isValid()) return;

    const oldMarkerMode = this.markerMode(); //  FIXED

    const isMobile = this.Map.getSize().x < 550;

    this.Map.fitBounds(bounds, {
      padding: global ? [200, 200] : [300, this.infoOverlay() ? 224 : 250],
      paddingTopLeft: global ? [200, 200] : [isMobile ? 25 : 380, this.infoOverlay() ? 224 : 250],
      paddingBottomRight: global ? [0, 0] : [Math.min(250, this.Map.getSize().x - 100), 100],
      animate: true,
      duration: 0.25,
    });

    setTimeout(() => {
      if (this.markerMode() !== oldMarkerMode && checkForMarkerModeChange) {
        this.fitBounds(global, false);
      }
    }, 0);
  }

  private refreshMapSize(delayMs = 0) {
    if (!this.Map) return;

    const run = () => {
      if (!this.Map) return;
      // pan:false prevents unwanted movement during resize
      this.Map.invalidateSize({ pan: false });
    };

    if (delayMs > 0) setTimeout(run, delayMs);
    else run();
  }

  private addMarkers(): void {
    if (!this.isInit() || !this.Map) return;

    // Clear old layer
    try {
      if (this.markers) {
        this.markers.clearLayers();
        this.Map.removeLayer(this.markers);
      }
    } catch (e) {
      logError(e);
    }

    const mode = this.markerMode(); // snapshot of current mode
    const plants = this.plants() ?? [];

    this.markerLatLngs = [];

    // Create cluster group
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 120,
      singleMarkerMode: false,
      spiderfyDistanceMultiplier: 15,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,

      spiderfyShapePositions: (count: number, centerPt: L.Point) => {
        const distanceFromCenter = 35;
        const markerDistance = 75;
        const lineLength = markerDistance * (count - 1);
        const lineStart = centerPt.y - lineLength / 2;
        const res: L.Point[] = new Array(count);

        for (let i = count - 1; i >= 0; i--) {
          res[i] = L.point(centerPt.x - distanceFromCenter, lineStart + markerDistance * i);
        }
        return res;
      },

      iconCreateFunction: (cluster: any) => {
        const childMarkers = cluster.getAllChildMarkers();

        const numWorking = childMarkers
          .map((m: any) => m?.options?.data?.working ?? 0)
          .reduce((a: number, b: number) => a + b, 0);

        const numWarning = childMarkers
          .map((m: any) => m?.options?.data?.warning ?? 0)
          .reduce((a: number, b: number) => a + b, 0);

        const numCritical = childMarkers
          .map((m: any) => m?.options?.data?.critical ?? 0)
          .reduce((a: number, b: number) => a + b, 0);

        const numInactive = childMarkers
          .map((m: any) => m?.options?.data?.inactive ?? 0)
          .reduce((a: number, b: number) => a + b, 0);

        const size =
          mode === MarkerMode.dots && numCritical === 0 ? ClustSizeByChildCount(numWorking) : 58;

        // Same HTML logic you had
        return mode === MarkerMode.dots
          ? L.divIcon({
              html:
                numCritical > 0
                  ? `<div class="loctite-marker-cluster critical drop-shadow"><span>${numCritical}</span></div>`
                  : numWorking > 0
                    ? `<div class="loctite-marker-cluster dot drop-shadow"><span>${numWorking}</span></div>`
                    : numInactive > 0
                      ? `<div class="loctite-marker-cluster inactive drop-shadow"><span>${numInactive}</span></div>`
                      : '',
              className: 'single-cluster',
              iconSize: L.point(size, size),
              iconAnchor: L.point(size / 2, size / 2),
              popupAnchor: L.point(0, -(size / 2) - 5),
            })
          : L.divIcon({
              html:
                (numWorking > 0
                  ? `<div class="loctite-marker-cluster drop-shadow"><span>${Number(numWorking)}</span></div>`
                  : '') +
                (numInactive > 0
                  ? `<div class="loctite-marker-cluster inactive drop-shadow"><span>${Number(numInactive)}</span></div>`
                  : '') +
                (numWarning > 0
                  ? `<div class="loctite-marker-cluster warning drop-shadow"><span>${Number(numWarning)}</span></div>`
                  : '') +
                (numCritical > 0
                  ? `<div class="loctite-marker-cluster critical drop-shadow"><span>${Number(numCritical)}</span></div>`
                  : ''),
              className: numWorking > 0 || numCritical > 0 ? 'multi-cluster' : 'single-cluster',
              iconSize: L.point(size, size),
              iconAnchor: L.point(size / 2, size / 2),
              popupAnchor: L.point(0, -(size / 2) - 5),
            });
      },
    });

    //  Attach cluster click ONCE (this is the correct place)
    clusterGroup.on('clusterclick', (a: any) => {
      if (!this.Map) return;

      const oldZoomLvl = this.Map.getZoom();
      const group = L.featureGroup(a.layer.getAllChildMarkers());
      const bounds = group.getBounds();
      const isMobile = this.Map.getSize().x < 550;

      this.Map.fitBounds(bounds, {
        padding: [100, 50],
        paddingTopLeft: [isMobile ? 25 : 80, this.infoOverlay() ? 224 : 100],
        paddingBottomRight: [Math.min(250, this.Map.getSize().x - 100), 100],
        animate: true,
        duration: 0.25,
      });

      const epsilon = 0.000001;
      const boundsWidth = Math.abs(bounds.getEast() - bounds.getWest());
      const boundsHeight = Math.abs(bounds.getNorth() - bounds.getSouth());

      if (boundsWidth < epsilon && boundsHeight < epsilon) {
        setTimeout(() => a.layer.spiderfy(), 200);
        return;
      }

      setTimeout(() => {
        if (!this.Map) return;

        if (this.Map.getZoom() === oldZoomLvl) {
          const epsilon2 = 0.001;

          if (boundsWidth < epsilon2 && boundsHeight < epsilon2) {
            setTimeout(() => a.layer.spiderfy(), 200);
          } else if (oldZoomLvl < ZOOM_MAX_THRESHOLD) {
            this.Map.setZoom(oldZoomLvl + 1);
          }
        }
      }, 300);
    });

    // Determine items to render based on marker mode
    const items =
      mode === MarkerMode.buildings
        ? plants
            .map((plant: any) =>
              (plant.children ?? []).map((building: any) => {
                if (!building.geoPosition) building.geoPosition = plant.geoPosition;
                return building;
              })
            )
            .flat(2)
        : plants;

    // Add markers
    for (const plant of items as any[]) {
      if (!plant?.geoPosition) continue;

      const [latStr, lonStr] = String(plant.geoPosition).split(';', 2);

      // Validate coordinate strings
      const regexLat = /^(-?[1-8]?\d(?:\.\d{1,18})?|90(?:\.0{1,18})?)$/;
      const regexLon = /^(-?(?:1[0-7]|[1-9])?\d(?:\.\d{1,18})?|180(?:\.0{1,18})?)$/;

      if (!regexLat.test(latStr) || !regexLon.test(lonStr)) continue;

      const lat = parseFloat(latStr);
      const lon = parseFloat(lonStr);

      const icon = this.createMarkerIcon(plant, mode);

      const marker = L.marker([lat, lon], {
        icon,
        //  IMPORTANT: put data on options so cluster iconCreateFunction can read marker.options.data
        data: {
          working: plant.Working?.length ?? 0,
          warning: plant.Warning?.length ?? 0,
          critical: plant.Critical?.length ?? 0,
          inactive: plant.InActive?.length ?? 0,
        },
      } as any);

      marker.on('click', (e: any) => {
        if (!this.Map) return;

        // Emit only in plant/building mode
        if (mode === MarkerMode.plants || mode === MarkerMode.buildings) {
          this.markerClick.emit(plant);
          return;
        }

        // Otherwise zoom into marker
        const isMobile = this.Map.getSize().x < 550;
        this.Map.fitBounds([e.latlng, e.latlng], {
          padding: [100, 50],
          paddingTopLeft: [isMobile ? 25 : 80, this.infoOverlay() ? 224 : 100],
          paddingBottomRight: [Math.min(250, this.Map.getSize().x - 100), 100],
          maxZoom:
            this.Map.getZoom() <= ZOOM_BUILDING_THRESHOLD - 1 ? ZOOM_BUILDING_THRESHOLD - 1 : -1,
          animate: true,
          duration: 0.25,
        });
      });

      clusterGroup.addLayer(marker);
      this.markerLatLngs.push([lat, lon]);
    }

    this.markers = clusterGroup;
    this.Map.addLayer(this.markers);

    // Safety: ensure marker cluster is actually on map
    setTimeout(() => {
      if (this.markers && !this.markers._map) this.markers = null;
    }, 0);
  }

  private createMarkerIcon(plant: any, mode: MarkerMode): L.DivIcon {
    const numWorking =
      (plant.Assets?.length ?? 0) -
      (plant.Critical?.length ?? 0) -
      (plant.Warning?.length ?? 0) -
      (plant.InActive?.length ?? 0);

    const numWarning = plant.Warning?.length ?? 0;
    const numCritical = plant.Critical?.length ?? 0;
    const numInactive = plant.InActive?.length ?? 0;

    const size =
      mode === MarkerMode.dots && numCritical === 0 && numWarning === 0 && numInactive === 0
        ? ClustSizeByChildCount(numWorking)
        : 58;

    if (mode === MarkerMode.dots) {
      const html =
        numCritical > 0
          ? `<div class="loctite-marker-cluster critical drop-shadow"><span>${Number(numCritical)}</span></div>`
          : numWarning > 0
            ? `<div class="loctite-marker-cluster warning drop-shadow"><span>${Number(numWarning)}</span></div>`
            : numInactive > 0
              ? `<div class="loctite-marker-cluster inactive drop-shadow"><span>${Number(numInactive)}</span></div>`
              : numWorking > 0
                ? `<div class="loctite-marker-cluster dot drop-shadow"><span>${Number(numWorking)}</span></div>`
                : '';

      return L.divIcon({
        html,
        className: 'single-cluster',
        iconSize: L.point(size, size),
        iconAnchor: L.point(size / 2, size / 2),
        popupAnchor: L.point(0, -(size / 2) - 5),
      });
    }

    if (mode === MarkerMode.circles) {
      return L.divIcon({
        html:
          (numWorking > 0
            ? `<div class="loctite-marker-cluster drop-shadow"><span>${Number(numWorking)}</span></div>`
            : '') +
          (numWarning > 0
            ? `<div class="loctite-marker-cluster warning drop-shadow"><span>${Number(numWarning)}</span></div>`
            : '') +
          (numInactive > 0
            ? `<div class="loctite-marker-cluster inactive drop-shadow"><span>${Number(numInactive)}</span></div>`
            : '') +
          (numCritical > 0
            ? `<div class="loctite-marker-cluster critical drop-shadow"><span>${Number(numCritical)}</span></div>`
            : ''),
        className: numWorking > 0 && numCritical > 0 ? 'multi-cluster' : 'single-cluster',
        iconSize: L.point(size, size),
        iconAnchor: L.point(size / 2, size / 2),
        popupAnchor: L.point(0, -(size / 2) - 5),
      });
    }

    // plants/buildings (rich marker html)
    if (plant.curLayer === 'c') {
      const markerClass =
        (numInactive > 0 && numWarning === 0 && numCritical === 0 ? ' inactive' : '') +
        (numWarning > 0 && numCritical === 0 ? ' warning' : '') +
        (numCritical > 0 ? ' critical' : '');

      return L.divIcon({
        className: 'custom-div-icon',
        html: `
    <div class="plant-marker-pin drop-shadow${markerClass}">
      <img
        class="plant-marker-thumbnail"
        src="${plant.thumbnail ?? ''}"
        />
        <div class='plant-marker-info'><span class="plant-marker-name h2">
          ${escapeHtml(plant.objectName)}
          [${plant.Assets?.length ?? 0}]
        </span>

        <span class="plant-marker-status">
          ${this.buildStatusText(plant)}
        </span>
      </div>
    </div>
  `,
        iconSize: [298, 58],
        iconAnchor: [29, 29],
      });
    }
    // building / area marker
    const buildingClass =
      (numInactive > 0 && numWarning === 0 && numCritical === 0 ? ' inactive' : '') +
      (numWarning > 0 && numCritical === 0 ? ' warning' : '') +
      (numCritical > 0 ? ' critical' : '');
    return L.divIcon({
      className: 'custom-div-icon',
      html: `
        <div class="building-marker-pin drop-shadow${buildingClass}">
          <div class="building-marker-counter body-bold">
            <span>
              ${
                plant.critical
                  ? numCritical
                  : plant.warning
                    ? numWarning
                    : plant.inactive
                      ? numInactive
                      : (plant.Assets?.length ?? 0)
              }
            </span>
          </div>

          <div class="building-marker-info">
            <span>
              ${escapeHtml(plant.objectName)}
              (${plant.Assets?.length ?? 0})
            </span>
          </div>
        </div>
      `,
      iconSize: [247, 62],
      iconAnchor: [29, 29],
    });
  }

  private buildStatusText(plant: any): string {
    const critical = plant.Critical?.length ?? 0;
    const warning = plant.Warning?.length ?? 0;
    const inactive = plant.InActive?.length ?? 0;

    if (critical > 0) {
      return `${critical} critical equipment${critical > 1 ? 's' : ''}`;
    }

    if (warning > 0) {
      return `${warning} warning equipment${warning > 1 ? 's' : ''}`;
    }

    if (inactive > 0) {
      return `${inactive} inactive equipment${inactive > 1 ? 's' : ''}`;
    }

    return 'All equipments working fine';
  }
}

function ClustSizeByChildCount(children: number): number {
  const min_count = 5;
  const max_count = 750;
  const min_size = 40;
  const max_size = 150;

  if (children < min_count) return min_size;
  if (children > max_count) return max_size;

  const factor = (children - min_count) / (max_count - min_count);
  return factor * (max_size - min_size) + min_size;
}

function escapeHtml(value: any): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
